import {
  createContext,
  useState,
  useCallback,
  useContext
} from 'react';
import { URL } from '../constants';
import {
  getCustomerServiceExistsType,
  updateCustomerServiceCall,
  createCustomerServiceCall
} from '../service/serviceChat/customerService';
import {
  crateRoom,
  createParticipants,
  createParticipant,
  deleteRoom
} from '../service/serviceChat/callMi4uService';
import {
  createSpace,
  closeSpace
} from '../service/serviceChat/callNeuronMeetService';
import {
  sendVideoTemplate,
  sendMessage,
  sendFiles,
  sendVideoBotucatu
} from '../service/serviceChat/chatService';
import {
  getParametersService
} from '../service/serviceChat/parametersService';
import { useAuth } from './auth';
import { useChat } from './chat';

import instructions from '../assets/image/instructions.jpeg';

const VideoCall = createContext();

const CALL_OPTIONS = {
  MI4U: 'VIDEOCHAMADA_MI4U',
  MEET: 'VIDEOCHAMADA_MEET',
  BOTUCATU: 'VIDEOCHAMADA_BOTUCATU'
};

const CALL_STATUS = {
  WAITING: 1,
  ACTIVE: 2,
  IN_CALL: 'IN_CALL',
  CLOSE_CALL: 'CLOSE_CALL',
  INVITED: 'INVITED'
};

const DEFAULT_CLOSE_MESSAGE = 'Video chamada encerrada !\nObrigado por participar.';

export const VideoCallProvider = ({ children }) => {

  const { searchUserLogged } = useAuth();
  const { handleMyChats, functionality, setCustomer } = useChat();

  const [dataCall, setDataCall] = useState(null);

  const onSetDataCall = useCallback((customer) => {
    const parsedCustomer = typeof customer !== 'string' ? customer : JSON.parse(customer);
    const newDataCall = {
      customer: parsedCustomer,
      call: JSON.parse(parsedCustomer?.link ?? '{}')
    };
    setDataCall(newDataCall);
    return newDataCall;
  }, []);
  const createInstructionsFile = useCallback(async (srcImg) => {
    try {
      const response = await fetch(srcImg);
      const blob = await response.blob();
      return new File([blob], srcImg);
    } catch (error) {
      console.error("Erro ao carregar a imagem:", error);
      throw error;
    }
  }, []);

  const onSendClientLink = useCallback(async (token, callOption, currentDataCall = dataCall, cd_it_agenda_central = "") => {
    if (!currentDataCall?.customer) return;

    if (callOption === CALL_OPTIONS.BOTUCATU) {
      const result = await sendVideoBotucatu(
        currentDataCall.customer.client_phone,
        currentDataCall.customer.id,
        currentDataCall.customer.client_id,
        cd_it_agenda_central,
        token
      );
      if (!result) throw new Error('Falha ao enviar link via Botucatu');
      return result;
    }

    if (functionality.includes('videochamada_botao')) {
      const result = await sendVideoTemplate(
        currentDataCall.customer.client_phone,
        currentDataCall.customer.id,
        token
      );
      if (!result) throw new Error('Falha ao enviar template de vídeo');
      return result;
    }

    const link = currentDataCall.call?.links?.client;
    const file = await createInstructionsFile(instructions);

    const result = await sendFiles(
      [file],
      `⚠️ Siga as instruções para a Video Chamada.\nPara participar acesse ao link:\n${link}`,
      currentDataCall.customer.client_phone,
      currentDataCall.customer.id
    );
    if (!result) throw new Error('Falha ao enviar arquivo de instruções');
    return result;
  }, [dataCall, functionality, createInstructionsFile]);
  const onSendMessage = useCallback(async (messageText, customerData = null) => {
    const customer = customerData || dataCall?.customer;
    if (!customer) return;
    return await sendMessage(
      customer.client_phone,
      'text',
      messageText,
      customer.id,
    );
  }, [dataCall]);
  const createParticipantsInMi4u = useCallback(async (currentDataCall) => {
    try {
      const hash = await crateRoom();
      const userLogged = await searchUserLogged();

      if (!userLogged?.id || !currentDataCall?.customer?.id) {
        throw new Error('Missing required user or customer data');
      }

      const payload = {
        hash,
        customer_service_id: currentDataCall.customer.id,
        user_id: userLogged.id,
        user_name: userLogged.nomecompleto,
        client_id: currentDataCall.customer.client_id,
        client_name: currentDataCall.customer.client_name
      };

      const participants = await createParticipants(payload);

      if (!participants?.user || !participants?.client) {
        throw new Error('Failed to create participants');
      }
      const { client, user } = participants
      return {
        user,
        client,
        hash
      };
    } catch (error) {
      console.error('Error creating participants in call:', error);
      throw error;
    }
  }, [searchUserLogged]);
  const startCallMi4u = useCallback(async (currentDataCall) => {
    const { user, client, hash } = await createParticipantsInMi4u(currentDataCall);

    const links = {
      user: `${URL.videoCallMi4u}${user?.token_call}/doctor`,
      client: `${URL.videoCallMi4u}${client?.token_call}`
    };

    const payload = {
      customer: { ...currentDataCall.customer },
      call: {
        links,
        hash,
        status: CALL_STATUS.IN_CALL
      }
    };

    try {
      await onSendClientLink(client?.token_call, CALL_OPTIONS.MI4U, payload);
    } catch (error) {
      console.error('Erro ao enviar link para o cliente:', error);
      if (hash) {
        await deleteRoom(hash).catch(console.error);
      }
      throw error;
    }

    setDataCall(payload);
    await updateCustomerServiceCall(payload, 'VIDEOCHAMADA');
    handleMyChats();
  }, [createParticipantsInMi4u, handleMyChats, onSendClientLink]);

  const startCallBotucatu = useCallback(async (currentDataCall, cd_it_agenda_central = "") => {
    // Se não tiver agenda selecionada, apenas chama a API para buscar as agendas
    // Não cria a sala nem envia link ainda
    if (!cd_it_agenda_central) {
      const result = await sendVideoBotucatu(
        currentDataCall.customer.client_phone,
        currentDataCall.customer.id,
        currentDataCall.customer.client_id,
        cd_it_agenda_central,
        "" // sem token ainda
      );
      return result;
    }

    const { user, client, hash } = await createParticipantsInMi4u(currentDataCall);

    const links = {
      user: `${URL.videoCallMi4u}${user?.token_call}/doctor`,
      client: `${URL.videoCallMi4u}${client?.token_call}`
    };

    const payload = {
      customer: { ...currentDataCall.customer },
      call: {
        links,
        hash,
        status: CALL_STATUS.IN_CALL
      }
    };

    let result;
    try {
      result = await onSendClientLink(client?.token_call, CALL_OPTIONS.BOTUCATU, payload, cd_it_agenda_central);
    } catch (error) {
      console.error('Erro ao enviar link para o cliente:', error);
      if (hash) {
        await deleteRoom(hash).catch(console.error);
      }
      throw error;
    }

    setDataCall(payload);
    await updateCustomerServiceCall(payload, 'VIDEOCHAMADA');
    handleMyChats();
    return result;
  }, [createParticipantsInMi4u, handleMyChats, onSendClientLink]);


  const startCallNeuronMeet = useCallback(async (currentDataCall) => {
    const { customer } = currentDataCall
    const space = await createSpace({
      user_id: customer?.usuario_id,
      customer_service_id: customer?.id
    });
    const links = {
      user: `${space?.link}`,
      client: `${space?.link}`
    };
    const payload = {
      customer,
      call: {
        links,
        hash: "NEURON_MEET",
        status: CALL_STATUS.IN_CALL
      }
    };
    const result = await onSendMessage(`⚠️ Esta chamada será realizada pelo Google Meet.\nPara participar, acesse o link:\n${space?.link}`, customer);
    if (!result) {
      await closeSpace(customer?.id).catch(console.error);
      throw new Error('Falha ao enviar link via Google Meet');
    }

    setDataCall(payload);
    await updateCustomerServiceCall(payload, 'VIDEOCHAMADA');
    handleMyChats();
  }, [handleMyChats, onSendMessage]);

  const openVideoCall = useCallback(async ({
    callOption,
    customer,
    cd_it_agenda_central = ""
  }) => {
    const currentDataCall = onSetDataCall(customer);

    switch (callOption) {
      case CALL_OPTIONS.MI4U:
        return await startCallMi4u(currentDataCall);
      case CALL_OPTIONS.MEET:
        return await startCallNeuronMeet(currentDataCall);
      case CALL_OPTIONS.BOTUCATU:
        return await startCallBotucatu(currentDataCall, cd_it_agenda_central);
      default:
        console.warn('Unknown call option:', callOption);
    }
  }, [onSetDataCall, startCallMi4u, startCallNeuronMeet, startCallBotucatu]);

  const resetCustomer = useCallback(async () => {
    const payload = {
      customer: { ...dataCall.customer },
      call: {
        status: CALL_STATUS.CLOSE_CALL
      }
    };
    await updateCustomerServiceCall(payload, 'NULL');
    setDataCall(null);
    setCustomer((prev) => {
      const newCustomer = { ...prev };
      delete newCustomer.link;
      return newCustomer;
    });
    handleMyChats();
  }, [dataCall, handleMyChats, setCustomer]);

  const messageCloseVideoCall = useCallback(async () => {
    try {
      const { data } = await getParametersService('MSG_END_VIDEO');

      if (data?.status_code === 400) {
        await onSendMessage(DEFAULT_CLOSE_MESSAGE);
      } else if (data?.result?.valor) {
        await onSendMessage(data.result.valor);
      } else {
        await onSendMessage(DEFAULT_CLOSE_MESSAGE);
      }
    } catch (error) {
      console.error('Error sending close message:', error);
      await onSendMessage(DEFAULT_CLOSE_MESSAGE);
    }
  }, [onSendMessage]);

  const closeVideoCall = useCallback(async (callOption) => {
    try {
      const { qtd_atendimento_aberto } = await getCustomerServiceExistsType(
        dataCall?.customer?.client_id,
        'VIDEOCHAMADA',
      );

      if (qtd_atendimento_aberto >= 2) {
        return await resetCustomer();
      }

      if (callOption === CALL_OPTIONS.MI4U) {
        const status = await deleteRoom(dataCall?.call?.hash);
        if (status === 200) {
          await messageCloseVideoCall();
        }
      }

      if (callOption === CALL_OPTIONS.MEET) {
        await closeSpace(dataCall?.customer?.id);
        await messageCloseVideoCall();
      }
      await resetCustomer();

      if (callOption === CALL_OPTIONS.BOTUCATU) {
        const status = await deleteRoom(dataCall?.call?.hash);
        if (status === 200) {
          await messageCloseVideoCall();
        }
      }
    } catch (error) {
      console.error('Error closing video call:', error);
      await resetCustomer();
    } finally {
      return true
    }
  }, [dataCall, resetCustomer, messageCloseVideoCall]);



  async function inviteUserSystem({ guestUser, rootCustomer }) {
    try {
      const { id, nomecompleto } = guestUser;
      const customerInvite = await createCustomerServiceCall(id, rootCustomer);
      const { data } = await createParticipant({
        hash: dataCall?.call?.hash ?? "",
        customer_service_id: customerInvite.id,
        user_id: id,
        client_id: 0,
        name: nomecompleto,
      });

      const links = {
        user: `${URL.videoCallMi4u}${data?.token_call}/doctor`,
        client: `NO_LINK`
      };
      const payload = {
        customer: customerInvite,
        call: {
          links,
          hash: dataCall?.call?.hash ?? "",
          status: CALL_STATUS.INVITED
        }
      };
      await updateCustomerServiceCall(payload, 'VIDEOCHAMADA');
      return true
    } catch (error) {
      console.error('Error inviting user:', error);
      return false
    }
  }

  async function inviteGuestExternal({ name, rootCustomer }) {
    const { id, client_id } = rootCustomer;
    const { data } = await createParticipant({
      hash: dataCall?.call?.hash ?? "",
      customer_service_id: id,
      user_id: 0,
      client_id: client_id,
      name: name,
    });
    return `${URL.videoCallMi4u}${data?.token_call}`;
  }

  async function acceptInvite(customer) {
    const link = JSON.parse(customer?.link)
    delete customer.link
    const payload = {
      customer: { ...customer },
      call: {
        ...link,
        status: CALL_STATUS.IN_CALL
      }
    };
    setDataCall(payload);
    await updateCustomerServiceCall(payload, 'VIDEOCHAMADA');
    handleMyChats();
  }


  const clearDataCall = useCallback(() => {
    setDataCall(null);
  }, []);

  const callValues = {
    openVideoCall,
    closeVideoCall,
    onSetDataCall,
    clearDataCall,
    dataCall,
    inviteUserSystem,
    inviteGuestExternal,
    acceptInvite
  };

  return <VideoCall.Provider value={callValues}>{children}</VideoCall.Provider>;
};

export function useVideoCall() {
  const context = useContext(VideoCall);

  if (!context) {
    throw new Error('useVideoCall must be used within a VideoCallProvider');
  }
  return context;
}
